import { useEventBus, type EventBusKey } from "@vueuse/core";
import { useId } from "reka-ui";
import {
  computed,
  isRef,
  onUnmounted,
  reactive,
  toValue,
  watch,
  watchEffect,
  type MaybeRef,
} from "vue";

export enum ModalEventType {
  Show = "Show",
  Hide = "Hide",
}

export interface ModalEvent {
  type: ModalEventType;
  modalId: string;
}

const modalEventBus: EventBusKey<ModalEvent> = Symbol("modalEventBus");

/**
 * Connects to the modal event bus to be able to keep a single modal open at a time.
 *
 * It registers a unique id for the modal (using the composable) and emits events when
 * the modal is shown and hidden including its id. So when components listen to these
 * events they can skip them or use them to hide itself if another modal is visible.
 */
export function useModalBus() {
  const bus = useEventBus(modalEventBus);

  function emitBusEvent(event: ModalEvent) {
    bus.emit(event);
  }

  function watchBusEvents(listener: (event: ModalEvent) => void) {
    return watchEffect(() => {
      bus.on(listener);

      return () => {
        bus.off(listener);
      };
    });
  }

  return {
    emitBusEvent,
    watchBusEvents,
  };
}

export function useJustOneOpenModal(isOpen: MaybeRef<boolean>) {
  const modalId = useId();
  const { emitBusEvent, watchBusEvents } = useModalBus();

  // Send update event to the bus when the modal is opened or closed
  watch(
    () => toValue(isOpen),
    (isOpenValue) => {
      if (isOpenValue) {
        emitBusEvent({ type: ModalEventType.Show, modalId });
      } else {
        emitBusEvent({ type: ModalEventType.Hide, modalId });
      }
    },
  );

  // When the component is unmounted and the modal is open, tell the bus it's "hidden"
  onUnmounted(() => {
    emitBusEvent({ type: ModalEventType.Hide, modalId });
  });

  // If another modal is shown, hide this one
  watchBusEvents((event) => {
    if (
      toValue(isOpen) &&
      event.type === ModalEventType.Show &&
      event.modalId !== modalId &&
      isRef(isOpen)
    ) {
      isOpen.value = false;
    }
  });

  return modalId;
}

export function useIsAnyModalOpen() {
  const { watchBusEvents } = useModalBus();
  const openModals = reactive(new Map<string, boolean>());
  const isAnyModalOpen = computed(() => openModals.size > 0);

  // If another modal is shown, hide this one
  watchBusEvents((event) => {
    if (event.type === ModalEventType.Show) {
      openModals.set(event.modalId, true);
    } else if (event.type === ModalEventType.Hide) {
      openModals.delete(event.modalId);
    }
  });

  return isAnyModalOpen;
}
